import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconBan, IconCalendarEvent, IconCheck, IconMessage, IconX } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import type { BoardStage } from '../../../entities/board';
import type { RuleRecord } from '../../../entities/rule';
import type { SmsTemplate } from '../../../entities/sms-template';
import type { Provider } from '../../../entities/provider';
import type { Group } from '../../../entities/group';
import { useAuth } from '../../auth';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Select, type SelectOption } from '../../../shared/ui/Select';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import {
  type RuleForm,
  type RuleType,
  type SmsEvent,
  type SmsRecipient,
  DATE_LEAD_FIELDS,
  EMPTY_RULE_FORM,
  LEAD_FIELDS,
  RULE_TYPE_OPTIONS,
  SMS_EVENT_OPTIONS,
  SMS_RECIPIENT_OPTIONS,
} from '../model/types';

interface RuleModalProps {
  mode: 'add' | 'edit';
  initial?: RuleRecord | null;
  stages: BoardStage[];
  onClose: () => void;
  onSaved: () => void;
}

const TYPE_ICON: Record<RuleType, React.ReactNode> = {
  RESTRICT:  <IconBan size={18} stroke={2} />,
  DATE_MOVE: <IconCalendarEvent size={18} stroke={2} />,
  SMS:       <IconMessage size={18} stroke={2} />,
};

const RuleModal: React.FC<RuleModalProps> = ({ mode, initial, stages, onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [excludeInitialOptions, setExcludeInitialOptions] = useState<SelectOption[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<RuleForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            ruleType: (initial.ruleType as RuleType) || 'RESTRICT',
            triggerColumn: initial.triggerColumn ?? '',
            targetColumn: initial.targetColumn ?? '',
            requiredFields: Array.isArray(initial.requiredFields) ? initial.requiredFields : [],
            dateField: initial.dateField ?? '',
            actual: initial.actual ?? true,
            smsName: (initial.data as Record<string, string> | null)?.name ?? '',
            smsEvent: ((initial.data as Record<string, string> | null)?.event as SmsEvent) ?? 'STAGE_CHANGE',
            smsTemplateId: initial.smsTemplateId ?? '',
            smsProviderId: initial.smsProviderId ?? '',
            smsRecipient: (initial.smsRecipient as SmsRecipient) ?? 'candidate',
            runOnce: initial.runOnce ?? false,
            groupIds: Array.isArray(initial.groupIds) ? initial.groupIds : [],
            excludeUserIds: Array.isArray(initial.excludeUserIds) ? initial.excludeUserIds : [],
          }
        : EMPTY_RULE_FORM,
  });

  const ruleType = watch('ruleType');
  const smsEvent = watch('smsEvent');
  const requiredFields = watch('requiredFields');
  const actual = watch('actual');
  const watchedGroupIds = watch('groupIds');

  // Load SMS data when type is SMS
  useEffect(() => {
    if (ruleType !== 'SMS') return;

    if (templates.length === 0) {
      pb.collection('sms_templates')
        .getFullList<SmsTemplate>({ sort: 'name', fields: 'id,name' })
        .then(setTemplates)
        .catch(() => {});
    }
    if (providers.length === 0) {
      pb.collection('provides')
        .getFullList<Provider>({ sort: 'name', fields: 'id,name,type', filter: 'actual=true' })
        .then(setProviders)
        .catch(() => {});
    }
    if (groups.length === 0) {
      const filter = isAdmin ? undefined : `managers ~ "${currentUser?.id}"`;
      pb.collection('groups')
        .getFullList<Group>({ sort: 'name', fields: 'id,name,managers,usersIds', filter })
        .then(setGroups)
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleType]);

  // Load initial options for excludeUserIds when editing
  useEffect(() => {
    if (mode !== 'edit' || !initial?.excludeUserIds?.length) return;
    pb.collection('users')
      .getList(1, 50, {
        filter: initial.excludeUserIds.map(id => `id="${id}"`).join('||'),
        fields: 'id,name',
      })
      .then(res => setExcludeInitialOptions(res.items.map(u => ({ value: u.id, label: u.name }))))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowedUserIds = useMemo<string[] | undefined>(() => {
    if (!watchedGroupIds.length) return undefined;
    const ids = new Set<string>();
    groups.filter(g => watchedGroupIds.includes(g.id)).forEach(g => {
      g.managers?.forEach(id => ids.add(id));
      g.usersIds?.forEach(id => ids.add(id));
    });
    return Array.from(ids);
  }, [watchedGroupIds, groups]);

  const toggleField = (field: string) => {
    const current = getValues('requiredFields');
    const next = current.includes(field) ? current.filter(f => f !== field) : [...current, field];
    setValue('requiredFields', next, { shouldDirty: true });
  };

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const isSms = values.ruleType === 'SMS';
      const payload: Record<string, unknown> = {
        ruleType: values.ruleType,
        actual: values.actual,
        // clear cross-type fields
        triggerColumn: isSms
          ? (values.smsEvent === 'STAGE_CHANGE' ? values.triggerColumn : '')
          : values.triggerColumn || '',
        targetColumn: isSms ? '' : values.targetColumn || '',
        requiredFields: values.requiredFields,
        dateField: isSms ? '' : values.dateField || '',
        data: isSms ? { name: values.smsName.trim(), event: values.smsEvent } : null,
        smsTemplateId: isSms ? values.smsTemplateId : '',
        smsProviderId: isSms ? values.smsProviderId : '',
        smsRecipient: isSms ? values.smsRecipient : '',
        runOnce: isSms ? values.runOnce : false,
        groupIds: isSms ? values.groupIds : [],
        excludeUserIds: isSms ? values.excludeUserIds : [],
      };

      if (mode === 'add') {
        await pb.collection('rulesBoard').create(payload);
        logAction('Created rule', 'ADD');
      } else {
        await pb.collection('rulesBoard').update(initial!.id, payload);
        logAction('Edited rule', 'EDIT');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: ruleType === 'SMS' ? 580 : 540 }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Создать правило' : 'Редактировать правило'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 18 }}>

          {/* Type selector */}
          <div className="adm-field">
            <label className="adm-label">Тип правила <span className="adm-req">*</span></label>
            <div className="adm-rule-type-grid">
              {RULE_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`adm-rule-type-btn${ruleType === opt.value ? ' active' : ''}`}
                  onClick={() => setValue('ruleType', opt.value, { shouldDirty: true, shouldValidate: true })}
                >
                  {TYPE_ICON[opt.value]}
                  <div>
                    <div className="adm-rule-type-label">{opt.label}</div>
                    <div className="adm-rule-type-desc">{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ── RESTRICT ───────────────────────────────────────── */}
          {ruleType === 'RESTRICT' && (
            <>
              <div className="adm-field">
                <label className="adm-label">Применяется при переходе В колонку <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="triggerColumn"
                  rules={{ required: 'Выберите колонку-триггер' }}
                  render={({ field }) => (
                    <Select
                      options={stages.map(s => ({ value: s.id, label: s.name }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={stages.length === 0 ? 'Загрузка...' : '— Выберите колонку —'}
                      disabled={stages.length === 0}
                    />
                  )}
                />
                {errors.triggerColumn?.message && <div className="form-field-error">{errors.triggerColumn.message}</div>}
              </div>
              <div className="adm-field">
                <label className="adm-label">
                  Обязательные поля
                  {requiredFields.length > 0 && <span className="adm-hint"> — выбрано {requiredFields.length}</span>}
                </label>
                <div className="adm-fields-grid">
                  {LEAD_FIELDS.map(f => {
                    const checked = requiredFields.includes(f.value);
                    return (
                      <button key={f.value} type="button" className={`adm-field-chip${checked ? ' active' : ''}`} onClick={() => toggleField(f.value)}>
                        {checked && <IconCheck size={11} stroke={2.5} />}
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* ── DATE_MOVE ──────────────────────────────────────── */}
          {ruleType === 'DATE_MOVE' && (
            <>
              <div className="adm-field">
                <label className="adm-label">Поле даты <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="dateField"
                  rules={{ required: 'Выберите поле даты' }}
                  render={({ field }) => (
                    <Select options={DATE_LEAD_FIELDS} value={field.value} onChange={field.onChange} placeholder="— Выберите поле —" />
                  )}
                />
                {errors.dateField?.message && <div className="form-field-error">{errors.dateField.message}</div>}
              </div>
              <div className="adm-field">
                <label className="adm-label">Перенести в колонку <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="targetColumn"
                  rules={{ required: 'Выберите целевую колонку' }}
                  render={({ field }) => (
                    <Select
                      options={stages.map(s => ({ value: s.id, label: s.name }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={stages.length === 0 ? 'Загрузка...' : '— Выберите колонку —'}
                      disabled={stages.length === 0}
                    />
                  )}
                />
                {errors.targetColumn?.message && <div className="form-field-error">{errors.targetColumn.message}</div>}
              </div>
            </>
          )}

          {/* ── SMS ───────────────────────────────────────────── */}
          {ruleType === 'SMS' && (
            <>
              {/* Название */}
              <div className="adm-field">
                <label className="adm-label">Название</label>
                <Input
                  className="adm-input"
                  type="text"
                  placeholder="Название правила"
                  autoComplete="off"
                  {...register('smsName')}
                />
              </div>

              {/* Событие */}
              <div className="adm-field">
                <label className="adm-label">Событие <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="smsEvent"
                  rules={{ required: 'Выберите событие' }}
                  render={({ field }) => (
                    <Select
                      options={SMS_EVENT_OPTIONS}
                      value={field.value}
                      onChange={v => field.onChange(v as SmsEvent)}
                    />
                  )}
                />
                {errors.smsEvent?.message && <div className="form-field-error">{errors.smsEvent.message}</div>}
              </div>

              {/* Этап — только для STAGE_CHANGE */}
              {smsEvent === 'STAGE_CHANGE' && (
                <div className="adm-field">
                  <label className="adm-label">Этап <span className="adm-req">*</span></label>
                  <Controller
                    control={control}
                    name="triggerColumn"
                    rules={{ required: smsEvent === 'STAGE_CHANGE' ? 'Выберите этап' : false }}
                    render={({ field }) => (
                      <Select
                        options={stages.map(s => ({ value: s.id, label: s.name }))}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={stages.length === 0 ? 'Загрузка...' : '— Выберите этап —'}
                        disabled={stages.length === 0}
                      />
                    )}
                  />
                  {errors.triggerColumn?.message && <div className="form-field-error">{errors.triggerColumn.message}</div>}
                </div>
              )}

              {/* Группы */}
              <div className="adm-field">
                <label className="adm-label">
                  Группы
                  <span className="adm-hint"> — пусто = все{!isAdmin ? ' (только ваши)' : ''}</span>
                </label>
                <Controller
                  control={control}
                  name="groupIds"
                  render={({ field }) => (
                    <Select
                      mode="multi"
                      options={groups.map(g => ({ value: g.id, label: g.name ?? g.id }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="— Все группы —"
                    />
                  )}
                />
              </div>

              {/* Шаблон SMS */}
              <div className="adm-field">
                <label className="adm-label">Шаблон SMS <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="smsTemplateId"
                  rules={{ required: 'Выберите шаблон' }}
                  render={({ field }) => (
                    <Select
                      options={templates.map(t => ({ value: t.id, label: t.name || t.id }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={templates.length === 0 ? 'Загрузка шаблонов...' : '— Выберите шаблон —'}
                      disabled={templates.length === 0}
                    />
                  )}
                />
                {errors.smsTemplateId?.message && <div className="form-field-error">{errors.smsTemplateId.message}</div>}
              </div>

              {/* Провайдер */}
              <div className="adm-field">
                <label className="adm-label">Провайдер <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="smsProviderId"
                  rules={{ required: 'Выберите провайдера' }}
                  render={({ field }) => (
                    <Select
                      options={providers.map(p => ({ value: p.id, label: `${p.name} (${p.type})` }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={providers.length === 0 ? 'Загрузка провайдеров...' : '— Выберите провайдера —'}
                      disabled={providers.length === 0}
                    />
                  )}
                />
                {errors.smsProviderId?.message && <div className="form-field-error">{errors.smsProviderId.message}</div>}
              </div>

              {/* Исключить пользователей */}
              <div className="adm-field">
                <label className="adm-label">
                  Исключить пользователей
                  <span className="adm-hint"> — SMS не отправляется при назначении этих людей</span>
                </label>
                <Controller
                  control={control}
                  name="excludeUserIds"
                  render={({ field }) => (
                    <UserSearchInput
                      key={allowedUserIds ? allowedUserIds.join(',') : 'all'}
                      mode="multi"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Найти пользователя..."
                      initialOptions={excludeInitialOptions}
                      allowedUserIds={allowedUserIds}
                      includeBlocked
                    />
                  )}
                />
              </div>

              {/* Получатель */}
              <div className="adm-field">
                <label className="adm-label">Получатель <span className="adm-req">*</span></label>
                <Controller
                  control={control}
                  name="smsRecipient"
                  rules={{ required: 'Выберите получателя' }}
                  render={({ field }) => (
                    <Select
                      options={SMS_RECIPIENT_OPTIONS}
                      value={field.value}
                      onChange={v => field.onChange(v as SmsRecipient)}
                    />
                  )}
                />
                {errors.smsRecipient?.message && <div className="form-field-error">{errors.smsRecipient.message}</div>}
              </div>

              {/* Один раз */}
              <div className="adm-field">
                <label className="adm-label">Отправить один раз</label>
                <Controller
                  control={control}
                  name="runOnce"
                  render={({ field }) => (
                    <button
                      type="button"
                      className={`adm-toggle${field.value ? ' on' : ''}`}
                      onClick={() => field.onChange(!field.value)}
                    >
                      <span className="adm-toggle-knob" />
                      <span className="adm-toggle-label">
                        {field.value ? 'Только один раз на лид' : 'Каждый раз при срабатывании'}
                      </span>
                    </button>
                  )}
                />
              </div>

              {/* Обязательные поля */}
              <div className="adm-field">
                <label className="adm-label">
                  Обязательные поля
                  <span className="adm-hint"> — если пусто, вместо SMS → ошибка в карточку</span>
                  {requiredFields.length > 0 && <span className="adm-hint"> ({requiredFields.length})</span>}
                </label>
                <div className="adm-fields-grid">
                  {LEAD_FIELDS.map(f => {
                    const checked = requiredFields.includes(f.value);
                    return (
                      <button key={f.value} type="button" className={`adm-field-chip${checked ? ' active' : ''}`} onClick={() => toggleField(f.value)}>
                        {checked && <IconCheck size={11} stroke={2.5} />}
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Статус */}
          <div className="adm-field">
            <label className="adm-label">Статус</label>
            <button
              type="button"
              className={`adm-toggle${actual ? ' on' : ''}`}
              onClick={() => setValue('actual', !actual, { shouldDirty: true })}
            >
              <span className="adm-toggle-knob" />
              <span className="adm-toggle-label">{actual ? 'Активно' : 'Отключено'}</span>
            </button>
          </div>

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>Отмена</Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { RuleModal };
